import { useNavigate } from 'react-router-dom'

import { Button, FormComponent, Icon, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { ApprovalsValueObjectsApprovalCategoryEnum } from 'generated/resources/datatable.graphql'
import { CreateApprovalGroupDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  onClose: () => void
  category: ApprovalsValueObjectsApprovalCategoryEnum
  illustration?: React.ComponentProps<typeof Icon.Large>['icon']
}

type CreateApprovalGroupFormValues = {
  autoApproved: boolean
  category: ApprovalsValueObjectsApprovalCategoryEnum
  description: string
  name: string
  companyId: number
}

const NewGroupModal = ({ onClose, category, illustration }: Props) => {
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()
  const nameLabel = i18n.t('settings.approvals.approval_group.new.title')
  const descriptionLabel = i18n.t('settings.approvals.approval_group.new.description')
  const navigate = useNavigate()

  const [createApprovalGroup, { loading: creatingApprovalGroup }] = useMutation(
    CreateApprovalGroupDocument,
    {
      extractorFn: (data) => data.approvalsMutations.createApprovalsApprovalGroup,
    }
  )

  const form = useForm<CreateApprovalGroupFormValues>({
    defaultValues: {
      autoApproved: false,
      category,
      description: '',
      name: '',
      companyId: company.id,
    },
    validate: (data) => {
      const errors: FormErrors<CreateApprovalGroupFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('common.validations.empty')
      }

      if (!data.description) {
        errors.description = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values) => {
      const result = await createApprovalGroup({
        variables: {
          autoApproved: values.autoApproved,
          category: values.category,
          description: values.description,
          name: values.name,
          companyId: values.companyId,
        },
      })
      const handled = handleResult(result, (errors) => {
        form.setErrors(errors)
      })

      if (handled.success && handled.data?.approvalGroup) {
        track(trackingEvents.approvals.addApprovalGroupSubmit)

        onClose()

        navigate(
          navigation.approvals.approvalGroupsCategoryId.fullPath({
            id: handled.data.approvalGroup.id,
            category,
          })
        )
      }
      return handled
    },
  })

  useEffect(() => {
    track(trackingEvents.approvals.addApprovalGroupStart)
  }, [])

  return (
    <Modal
      illustration={illustration}
      title={i18n.t('settings.approvals.approval_group.details.title')}
      description={i18n.t('settings.approvals.approval_group.new.modal_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.dirty || creatingApprovalGroup} submit>
            {i18n.t('settings.approvals.approval_group.new.save')}
          </Button>
        </>
      }
    >
      <FormComponent padding="s2">
        <TextField
          label={nameLabel}
          placeholder={nameLabel}
          value={form.values.name}
          onChange={(newValue) => form.setFieldValue('name', newValue)}
          error={form.touched.name ? form.errors.name : undefined}
        />
        <TextArea
          label={descriptionLabel}
          placeholder={descriptionLabel}
          value={form.values.description}
          onChange={(newValue) => form.setFieldValue('description', newValue)}
          error={form.touched.description ? form.errors.description : undefined}
        />
        <TogglerCheckbox
          labelWeight="medium"
          value={form.values.autoApproved}
          onChange={(newValue) => form.setFieldValue('autoApproved', newValue)}
          label={i18n.t('settings.approvals.approval_group.new.auto_approval')}
          information={i18n.t('settings.approvals.approval_group.new.auto_approval_tooltip')}
        />
      </FormComponent>
    </Modal>
  )
}

export default NewGroupModal
